/**
 * Favorites Page - Todo en Local
 * Displays user's favorite products
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Package, Trash2, ShoppingCart, Store, MapPin, Star, ArrowLeft } from 'lucide-react';
import { getProductImage } from '../../utils/constants';
import { toast } from 'sonner';
import api from '../../services/api';
import MarketplaceLayout from '../../components/marketplace/MarketplaceLayout';

// Brand colors
const COLORS = {
  navyBlue: '#1e3a5f',
  darkSlate: '#0f172a',
  orange: '#F37322'
};

const FavoritesPage = () => {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customerToken, setCustomerToken] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const token = localStorage.getItem('customer_token');
    if (!token) {
      toast.info('Inicia sesión para ver tus favoritos');
      navigate('/auth?redirect=/favoritos');
      return;
    }
    setCustomerToken(token);
    loadFavorites(token);
  }, [navigate]);

  const loadFavorites = async (token) => {
    try {
      setLoading(true);
      const response = await api.get(`/marketplace/favorites?customer_token=${token}`);
      setFavorites(response.data || []);
    } catch (error) {
      console.error('Error loading favorites:', error);
      toast.error('Error al cargar favoritos');
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (favoriteId) => {
    try {
      await api.delete(`/marketplace/favorites/${favoriteId}?customer_token=${customerToken}`);
      setFavorites(prev => prev.filter(f => f.favorite_id !== favoriteId));
      toast.success('Eliminado de favoritos');
    } catch (error) {
      toast.error('Error al eliminar de favoritos');
    }
  };

  const addToCart = async (favorite) => {
    try {
      const product = favorite.product || {};
      const productId = product.id || favorite.product_id;
      const businessId = product.business_id || favorite.business_id;
      let branchId = favorite.branch_id;
      
      // First, check if cart already has items - we must use the same branch
      const cartRes = await api.get(`/marketplace/cart?customer_token=${customerToken}`);
      const cartItems = cartRes.data.items || [];
      
      if (cartItems.length > 0) {
        const cartBranchId = cartItems[0].branch_id;
        const cartBusinessId = cartItems[0].business_id;
        
        // Verify same business
        if (cartBusinessId !== businessId) {
          toast.error('Solo puedes agregar productos de la misma tienda al carrito. Vacía el carrito primero para comprar en otra tienda.');
          return;
        }
        
        // Use the same branch as existing cart items
        branchId = cartBranchId;
      } else if (!branchId && businessId) {
        // Cart is empty and no branch saved - get first available branch
        try {
          const storeRes = await api.get(`/marketplace/stores/${businessId}`);
          const branches = storeRes.data.branches || [];
          if (branches.length > 0) {
            branchId = branches[0].id;
          }
        } catch (err) {
          console.error('Error getting branches:', err);
        }
      }
      
      if (!branchId) {
        toast.error('No se pudo encontrar una sucursal disponible');
        return;
      }
      
      await api.post(`/marketplace/cart/add?customer_token=${customerToken}`, {
        product_id: productId,
        business_id: businessId,
        branch_id: branchId,
        quantity: 1
      });
      toast.success('Producto agregado al carrito');
      window.dispatchEvent(new Event('cart-updated'));
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al agregar al carrito';
      toast.error(msg);
    }
  };

  return (
    <MarketplaceLayout showFloatingCart={true}>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Heart className="w-6 h-6 text-red-500 fill-red-500" />
              Mis Favoritos
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {favorites.length} {favorites.length === 1 ? 'producto guardado' : 'productos guardados'}
            </p>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden animate-pulse">
                <div className="aspect-square bg-slate-200" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-slate-200 rounded w-3/4" />
                  <div className="h-4 bg-slate-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : favorites.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-slate-100 flex items-center justify-center">
              <Heart className="w-10 h-10 text-slate-300" />
            </div>
            <h2 className="text-xl font-semibold text-slate-800 mb-2">No tienes favoritos aún</h2>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              Explora productos y toca el corazón para guardarlos aquí. Así podrás encontrarlos fácilmente después.
            </p>
            <Link
              to="/buscar"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium"
              style={{ backgroundColor: COLORS.orange }}
            >
              <Package className="w-5 h-5" />
              Explorar productos
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {favorites.map((favorite) => {
              // Extract product data - handle both nested and flat structures
              const product = favorite.product || {};
              const productId = product.id || favorite.product_id;
              const productName = product.name || favorite.product_name || 'Producto';
              const productImage = product.image_url || favorite.product_image;
              const productPrice = product.price || product.retail_price || favorite.product_price || 0;
              const businessId = product.business_id || favorite.business_id;
              const businessName = favorite.business_name || 'Tienda';
              
              return (
                <div 
                  key={favorite.favorite_id}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow group"
                  data-testid={`favorite-item-${favorite.favorite_id}`}
                >
                  {/* Image */}
                  <Link to={`/producto/${productId}`} className="block relative">
                    <div className="aspect-square bg-slate-50 overflow-hidden">
                      <img
                        src={getProductImage(productImage)}
                        alt={productName}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        onError={(e) => { e.target.src = getProductImage(); }}
                      />
                    </div>
                    <button
                      onClick={(e) => { e.preventDefault(); removeFavorite(favorite.favorite_id); }}
                      className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors"
                      title="Eliminar de favoritos"
                    >
                      <Heart className="w-4 h-4 text-red-500 fill-red-500" />
                    </button>
                  </Link>

                  {/* Info */}
                  <div className="p-4">
                    <Link to={`/producto/${productId}`}>
                      <h3 className="font-medium text-slate-800 line-clamp-2 hover:text-orange-500 transition-colors">
                        {productName}
                      </h3>
                    </Link>
                    
                    <p className="text-lg font-bold mt-2" style={{ color: COLORS.navyBlue }}>
                      ${productPrice.toFixed(2)}
                    </p>

                    {/* Store info */}
                    <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                      <Store className="w-3 h-3" />
                      <span className="truncate">{businessName}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => addToCart({ ...favorite, product_id: productId, business_id: businessId })}
                        className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-white font-medium text-sm transition-colors"
                        style={{ backgroundColor: COLORS.orange }}
                        data-testid={`add-to-cart-${favorite.favorite_id}`}
                      >
                        <ShoppingCart className="w-4 h-4" />
                        Agregar
                      </button>
                      <button
                        onClick={() => removeFavorite(favorite.favorite_id)}
                        className="p-2 border border-slate-200 rounded-lg hover:bg-red-50 hover:border-red-200 transition-colors"
                        title="Eliminar"
                        data-testid={`remove-favorite-${favorite.favorite_id}`}
                      >
                        <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Back to shopping link */}
        {favorites.length > 0 && (
          <div className="text-center mt-12">
            <Link
              to="/buscar"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
            >
              <Package className="w-5 h-5" />
              Seguir explorando
            </Link>
          </div>
        )}
      </div>
    </MarketplaceLayout>
  );
};

export default FavoritesPage;
